'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import type { TarotCard } from '@/data/tarotData';
import SpeechRecognitionController from './SpeechRecognitionController';
import MonthSelectorView from './MonthSelectorView';

const DAY_HEADERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  referenceDate: Date;
  today: Date;
  weekDays: Date[];
  isListening: boolean;
  onListeningChange: (v: boolean) => void;
  onVoiceOverride: (date: Date) => void;
  onVoiceReset: (date: Date) => void;
  onVoiceNotRecognized?: () => void;
  overrides: Record<string, TarotCard>;
  /** Speech recognition max wait in ms. Defaults to 2 minutes. */
  speechDelayMs?: number;
  /** Called when user single-clicks and selects a month from the month selector */
  onNavigateToMonth?: (year: number, month: number) => void;
  /** Called when the month selector opens or closes */
  onMonthSelectorToggle?: (open: boolean) => void;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  let d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

export default function MiniMonthCalendar({
  referenceDate, today, weekDays, isListening, onListeningChange,
  onVoiceOverride, onVoiceReset, onVoiceNotRecognized, overrides, speechDelayMs,
  onNavigateToMonth, onMonthSelectorToggle,
}: Props) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOffset = getFirstDayOfMonth(year, month);
  const prevMonthDays = getDaysInMonth(year, month - 1);

  // Build calendar grid (always 6 rows × 7 cols)
  const cells: { day: number; isCurrentMonth: boolean; dateObj: Date }[] = [];

  // Previous month overflow
  for (let i = firstDayOffset - 1; i >= 0; i--) {
    let d = prevMonthDays - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, isCurrentMonth: false, dateObj: new Date(y, m, d) });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, isCurrentMonth: true, dateObj: new Date(year, month, d) });
  }

  // Next month overflow
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, isCurrentMonth: false, dateObj: new Date(y, m, d) });
  }

  // Long press logic
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pressActive, setPressActive] = useState(false);
  const didLongPress = useRef(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);

  const startLongPress = useCallback(() => {
    // If already listening, any press (short or long) will stop — handled in handleClick
    if (isListening) return;
    setPressActive(true);
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      onListeningChange(true);
      setPressActive(false);
    }, 700);
  }, [isListening, onListeningChange]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setPressActive(false);
  }, []);

  // Click handler:
  //   - If currently listening: any click (short or long) stops recognition, no popup
  //   - If not listening and no long press fired: open month selector
  const handleClick = useCallback(() => {
    if (isListening) {
      // Stop recognition — do NOT open month selector
      onListeningChange(false);
      didLongPress.current = false;
      return;
    }
    if (!didLongPress.current) {
      // Small delay prevents the touch that opened the selector from
      // immediately triggering a ghost-click on a month button inside it
      // (common on Android where touchend → click fires on the new element).
      setTimeout(() => {
        setShowMonthSelector(true);
        onMonthSelectorToggle?.(true);
      }, 50);
    }
    didLongPress.current = false;
  }, [isListening, onListeningChange, onMonthSelectorToggle]);

  const handleMonthSelected = useCallback((selYear: number, selMonth: number) => {
    setShowMonthSelector(false);
    onMonthSelectorToggle?.(false);
    onNavigateToMonth?.(selYear, selMonth);
  }, [onNavigateToMonth, onMonthSelectorToggle]);

  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  // Stable callbacks for SpeechRecognitionController
  const handleSpeechResult = useCallback((date: Date) => {
    onListeningChange(false);
    onVoiceOverride(date);
  }, [onListeningChange, onVoiceOverride]);

  const handleSpeechStop = useCallback(() => {
    onListeningChange(false);
    onVoiceNotRecognized?.();
  }, [onListeningChange, onVoiceNotRecognized]);

  const weekDayTimes = new Set(weekDays.map(d => d.toDateString()));
  const todayStr = today.toDateString();

  return (
    <>
      <div
        className={`
          relative rounded-xl border overflow-hidden transition-all duration-300 h-full
          bg-card card-glow
          ${isListening ? 'listening-pulse border-accent/60' : 'border-border'}
          ${pressActive ? 'scale-[0.98]' : ''}
          cursor-default select-none
        `}
        onMouseDown={startLongPress}
        onMouseUp={(e) => { cancelLongPress(); handleClick(); }}
        onMouseLeave={cancelLongPress}
        onTouchStart={(e) => { e.stopPropagation(); startLongPress(); }}
        onTouchEnd={(e) => { e.stopPropagation(); cancelLongPress(); handleClick(); }}
        onTouchCancel={(e) => { e.stopPropagation(); cancelLongPress(); }}
        role="complementary"
        aria-label="Mini monthly calendar. Tap to select month, long press to activate voice recognition."
      >
        {/* Corner ornaments */}
        <span className="absolute top-1.5 left-1.5 text-primary/20 text-xs select-none" aria-hidden>✦</span>
        <span className="absolute top-1.5 right-1.5 text-primary/20 text-xs select-none" aria-hidden>✦</span>

        <div className="p-2 sm:p-3 flex flex-col h-full gap-1">

          {/* Month / Year header */}
          <div className="flex-shrink-0 flex flex-col items-center gap-0.5 pb-0.5">
            <div className="flex items-center gap-2">
              <span className="text-primary/40 text-xs" aria-hidden>✦</span>
              <h2 className="font-display text-xs sm:text-sm text-primary tracking-widest uppercase font-semibold">
                {MONTH_NAMES_SHORT[month]}
              </h2>
              <span className="font-display text-[10px] text-muted-foreground tracking-wider">
                {year}
              </span>
              <span className="text-primary/40 text-xs" aria-hidden>✦</span>
            </div>
            <div className="ornament-line opacity-40" />
          </div>

          {/* Day headers */}
          <div className="flex-shrink-0 grid grid-cols-7 gap-0">
            {DAY_HEADERS.map((h, i) => (
              <div
                key={`mini-hdr-${i}`}
                className="text-center font-display text-[8px] sm:text-[9px] text-muted-foreground/60 tracking-wider uppercase py-0.5"
              >
                {h}
              </div>
            ))}
          </div>

          {/* Day cells — flex-1 to fill remaining space */}
          <div className="grid grid-cols-7 gap-0 flex-1">
            {cells.map((cell, idx) => {
              const cellDateStr = cell.dateObj.toDateString();
              const isToday = cellDateStr === todayStr;
              const isInWeek = weekDayTimes.has(cellDateStr);
              const hasOverride = cell.isCurrentMonth && overrides[cell.dateObj.toISOString().split('T')[0]];

              return (
                <div
                  key={`mini-cell-${idx}`}
                  className={`
                    flex items-center justify-center
                    font-display text-[8px] sm:text-[9px] lg:text-[10px]
                    transition-colors duration-150 rounded-sm
                    ${isToday
                      ? 'mini-day-today'
                      : isInWeek && cell.isCurrentMonth
                      ? 'mini-day-selected-week text-primary'
                      : cell.isCurrentMonth
                      ? 'text-foreground/75 hover:text-primary hover:bg-muted/40'
                      : 'text-muted-foreground/25 opacity-50'
                    }
                  `}
                >
                  <span className="relative">
                    {cell.day}
                    {hasOverride && (
                      <span className="absolute -top-0.5 -right-1 w-1 h-1 rounded-full bg-accent" aria-hidden />
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Voice indicator / hint */}
          <div className="flex-shrink-0 pt-0.5">
            <div className="arcane-divider opacity-30 mb-1" />
            {/*isListening ? (
              <div className="flex items-center justify-center gap-2">
                <div className="relative w-2 h-2">
                  <div className="absolute inset-0 rounded-full bg-accent voice-ring" />
                  <div className="w-2 h-2 rounded-full bg-accent" />
                </div>
                <span className="font-body text-[8px] text-accent italic animate-pulse">
                  Listening…
                </span>
              </div>
            ) : (
              <p className="text-center font-body text-[7px] sm:text-[8px] text-muted-foreground/40 italic">
                Tap · month view &nbsp;|&nbsp; Hold · voice
              </p>
            )*/}
          </div>
        </div>

        {/* Speech Recognition Controller */}
        <SpeechRecognitionController
          isActive={isListening}
          onResult={handleSpeechResult}
          onStop={handleSpeechStop}
          onReset={onVoiceReset}
          maxListenMs={speechDelayMs}
        />
      </div>

      {/* Month Selector View */}
      {showMonthSelector && (
        <MonthSelectorView
          referenceDate={referenceDate}
          today={today}
          onSelectMonth={handleMonthSelected}
          onClose={() => { setShowMonthSelector(false); onMonthSelectorToggle?.(false); }}
        />
      )}
    </>
  );
}